import dataSnapshot from '../redux/data.json'

const now = new Date()

function iso(minutesOffset = 0) {
  return new Date(now.getTime() + minutesOffset * 60 * 1000).toISOString()
}

function createConnection(items) {
  return {
    pageInfo: {
      hasNextPage: false,
    },
    edges: items.map((item, index) => ({
      cursor: item.cursor || `${item.id || index}`,
      node: item,
    })),
  }
}

const defaultDataType = {
  id: 'datatype-1',
  name: 'Invoice',
  description: 'Sample invoice payload',
}

const defaultSender = {
  id: 'sender-1',
  name: 'Mock Sender',
  description: 'Demonstration sender',
}

const defaultReceiver = {
  id: 'receiver-1',
  name: 'Mock Receiver',
  description: 'Demonstration receiver',
}

const defaultIntegration = {
  id: 'integration-1',
  name: 'Mock Integration',
  description: 'Sample integration for local development',
}

const defaultDataSource = {
  id: 'datasource-1',
  name: 'Mock SFTP Source',
  description: 'Sample SFTP source',
  type: 'SFTP',
  configuration: {
    host: 'sftp.local',
    port: 22,
    username: 'demo',
  },
}

const mockJobs = [
  {
    id: 'job-1',
    status: 'DELIVERED',
    creationTimestamp: iso(-120),
    deliveryTimestamp: iso(-90),
    retriable: true,
    retries: [],
    integration: { name: defaultIntegration.name },
    sender: { id: defaultSender.id, name: defaultSender.name, description: defaultSender.description },
    receiver: { id: defaultReceiver.id, name: defaultReceiver.name, description: defaultReceiver.description },
    dataType: { id: defaultDataType.id, name: defaultDataType.name, description: defaultDataType.description },
    parents: [],
  },
]

const mockJobDetail = {
  ...mockJobs[0],
  cancellationTimestamp: null,
  failureTimestamp: null,
  fileSize: 1024 * 1024,
  scheduledDelivery: iso(-95),
  nextActionTime: null,
  payloadHistory: [
    {
      component: 'DELIVERY_MANAGER',
      payloadUrl: 'https://storage.local/payloads/job-1.json',
    },
  ],
  sftpGatewayLog: {
    id: 'sftp-log-1',
  },
  children: [],
  batchedJobs: [],
  retriedFrom: null,
  retries: [],
  metadata: JSON.stringify({ customer: 'Mock Retailer', referenceId: 'REF-001' }),
  logs: createConnection([
    {
      businessLog: {
        type: 'INFO',
        message: 'Mock job processed successfully.',
        timestamp: iso(-100),
      },
      component: 'DELIVERY_MANAGER',
      timestamp: iso(-100),
      region: 'local-primary',
      failed: false,
    },
  ]),
  dataSource: defaultDataSource,
  integration: defaultIntegration,
  sender: defaultSender,
  receiver: defaultReceiver,
  dataType: defaultDataType,
  stagedPayloadUrl: 'https://storage.local/payloads/job-1.json',
}

const manualPayloads = [
  { payloadUrl: 'https://storage.local/manual/sample-1.json' },
  { payloadUrl: 'https://storage.local/manual/sample-2.json' },
]

const gatewayLogConnection = createConnection([
  {
    id: 'sftp-log-1',
    failed: false,
    timestamp: iso(-80),
    executionStartTime: iso(-82),
    dataSource: { id: defaultDataSource.id },
  },
])

const gatewayLogDetail = {
  id: 'sftp-log-1',
  failed: false,
  timestamp: iso(-80),
  executionStartTime: iso(-82),
  logURL: 'https://storage.local/logs/sftp-log-1.json',
  businessLog: [
    {
      type: 'INFO',
      timestamp: iso(-81),
      message: 'Gateway processed payload successfully.',
    },
  ],
  dataSource: {
    id: defaultDataSource.id,
    name: defaultDataSource.name,
    description: defaultDataSource.description,
  },
}

const endpointConnection = createConnection([
  {
    id: 'endpoint-1',
    name: 'Mock Endpoint',
    description: 'Sample endpoint',
    type: 'BLOB',
  },
])

const userConnection = createConnection([
  {
    id: 'user-1',
    email: 'local-admin@example.com',
    role: 'admin',
  },
])

const componentTypes = [
  { component: 'DELIVERY_MANAGER', description: 'Mock delivery manager component' },
  { component: 'SFTP_GATEWAY', description: 'Mock SFTP gateway component' },
]

const failureConfig = {
  componentFailureConfigs: componentTypes.map(({ component }) => ({ component, failureRate: 0 })),
}

const maintenanceTemplateConnection = createConnection([])
const maintenanceConnection = createConnection([])

function wrapData(fieldName, value) {
  return {
    data: {
      [fieldName]: value,
    },
  }
}

export function mockQuery(fieldName, variables = {}) {
  switch (fieldName) {
    case 'manualPayloads':
      return wrapData(fieldName, manualPayloads)
    case 'dataSystems': {
      const systems = dataSnapshot.dataSystems.map((system) => ({ id: `${system.id}`, name: system.name }))
      return wrapData(fieldName, createConnection(systems))
    }
    case 'dataSystem':
      return wrapData(fieldName, {
        id: `${variables.id || 'dataSystem-1'}`,
        name: 'Mock Data System',
        description: 'Sample data system',
        deliveryConfigs: createConnection([]),
      })
    case 'dataTypes': {
      const types = dataSnapshot.dataTypes.map((type) => ({ id: `${type.id}`, name: type.name }))
      return wrapData(fieldName, createConnection(types))
    }
    case 'dataType':
      return wrapData(fieldName, defaultDataType)
    case 'dataSources': {
      const sources = [
        {
          id: defaultDataSource.id,
          name: defaultDataSource.name,
          description: defaultDataSource.description,
          type: defaultDataSource.type,
        },
      ]
      return wrapData(fieldName, createConnection(sources))
    }
    case 'dataSource':
      return wrapData(fieldName, {
        ...defaultDataSource,
        sftpGatewayLogs: gatewayLogConnection,
        ftpGatewayLogs: createConnection([]),
      })
    case 'integrations': {
      const integrations = dataSnapshot.integrations.map((integration) => ({
        id: `${integration.id}`,
        name: integration.sender || integration.receiver || `Integration ${integration.id}`,
      }))
      return wrapData(fieldName, createConnection(integrations))
    }
    case 'integration':
      return wrapData(fieldName, defaultIntegration)
    case 'jobs':
      return wrapData(fieldName, createConnection(mockJobs))
    case 'job':
      return wrapData(fieldName, mockJobDetail)
    case 'pendingBatchJobs':
      return wrapData(fieldName, [
        {
          job: {
            id: mockJobs[0].id,
            creationTimestamp: mockJobs[0].creationTimestamp,
            sender: { id: defaultSender.id, name: defaultSender.name },
            receiver: { id: defaultReceiver.id, name: defaultReceiver.name },
            dataType: { id: defaultDataType.id, name: defaultDataType.name },
          },
          nextActionTime: iso(30),
        },
      ])
    case 'metadataKeys':
      return wrapData(fieldName, createConnection([]))
    case 'gatewaySchedulerLogs':
    case 'batchDeliveryManagerLogs':
    case 'sftpGatewayLogs':
      return wrapData(fieldName, gatewayLogConnection)
    case 'gatewaySchedulerLog':
    case 'batchDeliveryManagerLog':
    case 'sftpGatewayLog':
    case 'ftpGatewayLog':
      return wrapData(fieldName, gatewayLogDetail)
    case 'endpoints':
      return wrapData(fieldName, endpointConnection)
    case 'endpoint':
      return wrapData(fieldName, endpointConnection.edges[0].node)
    case 'users':
      return wrapData(fieldName, userConnection)
    case 'user':
      return wrapData(fieldName, userConnection.edges[0].node)
    case 'componentTypes':
      return wrapData(fieldName, componentTypes)
    case 'failureConfig':
      return wrapData(fieldName, failureConfig)
    case 'currentUser':
      return wrapData(fieldName, {
        id: 'user-1',
        email: 'local-admin@example.com',
        role: 'admin',
      })
    case 'systemMaintenances':
      return wrapData(fieldName, maintenanceConnection)
    case 'systemMaintenanceTemplates':
      return wrapData(fieldName, maintenanceTemplateConnection)
    default:
      return wrapData(fieldName, null)
  }
}

export function mockMutation(fieldName, variables = {}) {
  switch (fieldName) {
    case 'sendToManualGateway':
      return wrapData(fieldName, {
        errorMessage: null,
        job: {
          id: mockJobs[0].id,
          status: mockJobs[0].status,
        },
      })
    case 'retryJob':
    case 'failJob':
      return wrapData(fieldName, {
        jobId: variables.input?.jobId || mockJobs[0].id,
        errorMessage: null,
      })
    case 'addSystemMaintenance':
      return wrapData(fieldName, {
        id: variables.input?.id || 'maintenance-1',
      })
    case 'failureConfig':
      return wrapData('failureConfig', failureConfig)
    case 'addSystemMaintenanceTemplate':
      return wrapData('addSystemMaintenanceTemplate', {
        success: true,
        errorMessage: null,
      })
    case 'deleteSystemMaintenanceTemplate':
    case 'deleteSystemMaintenance':
      return wrapData(fieldName, {
        id: variables.id || 'mock-id',
        errorMessage: null,
      })
    case 'addUser':
      return wrapData(fieldName, {
        id: 'user-2',
        errorMessage: null,
      })
    case 'updateUser':
      return wrapData(fieldName, {
        id: 'user-1',
        errorMessage: null,
      })
    case 'deleteUser':
      return wrapData(fieldName, {
        id: variables.id || 'user-1',
        errorMessage: null,
      })
    default:
      return wrapData(fieldName, {
        success: true,
        errorMessage: null,
      })
  }
}
